import { useAnimations, Box } from "@react-three/drei";
import { useDracoGLTF } from "../../../../utils/useDracoGLTF";
import { useMemo } from "react";

import { findAllObjectsStartsWithKey, findObjectStartsWithKey } from "../../../../utils/FindObjects";
import useHashLocation from "../../../../use-hash-location";
import { useTranslation } from "react-i18next";
import Hotspot from "../../../ui/Hotspot";
import SinacInteraction from "./SinacInteraction";
import DynamicTube from "../../DynamicTube";
import { useRemapMaterials } from "../../Materials";
import { Ebbne, MountWithEbbne, useEbbne } from "@solidwhite/ebbnen";
import EbbnenLabel from "../../EbbnenLabel";
import SetCameraTarget from "../../SetCameraTarget";
import { SideBarHider } from "../../../ui/SideBar/SideBar";

const Sinac = () => {
  const { nodes, scene, animations } = useDracoGLTF("./models/heat/Sinac.glb");

  useRemapMaterials(scene);
  const { actions } = useAnimations(animations, scene);

  const [_, setLocation] = useHashLocation();

  const { t } = useTranslation();

  const hotSpots = useMemo(() => {
    var hotspots = [];
    var hsParent;
    hsParent = findObjectStartsWithKey("Labels_Root", scene);

    if (hsParent) {
      hsParent.children.forEach((c) => hotspots.push(c));
    }

    return hotspots;
  }, []);

  const handleLabelClick = (e, hotspot) => {
    e.stopPropagation();
    //TODO reenable with 2nd section
    // setLocation(`/heating/${hotspot.userData.targetId}`);
    setLocation(`/${hotspot.userData.targetId}`);
  };

  const cameraTargets = useMemo(() => {
    var obj = findObjectStartsWithKey("CameraTargets_All", scene);
    var camTargets = {};
    obj.children.forEach((c) => {
      if (c.userData.route) camTargets[c.userData.route] = c;
    });

    return camTargets;
  });

  const tubePoints = useMemo(() => {
    var points = findAllObjectsStartsWithKey("Tube_Point", scene);

    points.sort((a, b) => {
      var intA = parseInt(a.name.replace("Tube_Point_", ""));
      var intB = parseInt(b.name.replace("Tube_Point_", ""));

      if (intA < intB) {
        return -1;
      }
      if (intA > intB) {
        return 1;
      }
      return 0;
    });

    return points;
  }, []);

  return (
    <group>
      <primitive object={scene} />

      <MountWithEbbne>
        <group position={nodes.Sinac_1.position} rotation={nodes.Sinac_1.rotation} scale={nodes.Sinac_1.scale}>
          <Hotspot
            position={hotSpots[1].position}
            text={hotSpots[1].userData.textId ? t(hotSpots[1].userData.textId) : hotSpots[1].name.toUpperCase()}
            topLeft
            onClick={(e) => handleLabelClick(e, hotSpots[1])}
            delay={0}
            id={hotSpots[1].name}
            key={hotSpots[1].name}
          />
        </group>

        <SetCameraTarget settingsObject={cameraTargets["/products/sinac"]} />
      </MountWithEbbne>

      <Ebbne id="interact" visibleWithAbove={3}>
        <MountWithEbbne mountedWhen={"visible-and-below"}>
          <SinacTube points={tubePoints} />
        </MountWithEbbne>

        <MountWithEbbne>
          <SideBarHider />
          <SetCameraTarget settingsObject={cameraTargets["/products/sinac/interact"]} />
          <SinacInteraction action={actions["Roboter_Anim"]} scene={scene} />
        </MountWithEbbne>

        <group position={nodes.Sinac_1.position} rotation={nodes.Sinac_1.rotation} scale={nodes.Sinac_1.scale}>
          <EbbnenLabel hotspot={hotSpots[0]} isAnimation />
        </group>
      </Ebbne>
    </group>
  );
};

export const SinacTube = ({ points, update = false }) => {
  const { active } = useEbbne();

  return <DynamicTube pointRefs={points} segments={100} update={active} tubeRadius={0.025} />;
};

export default Sinac;
